<template>
  <v-navigation-drawer
    app
    :temporary="$vuetify.breakpoint.smAndDown"
    class="app--drawer"
    :mini-variant.sync="mini"
    v-model="drawer"
    :width="drawWidth"
    dark
    color="#0f172a"
  >
    <div class="drawer-brand">
      <img
        :src="computeLogo"
        height="40"
        alt="Vanguard Transit"
        class="drawer-brand__logo"
      />
      <button
        v-if="$vuetify.breakpoint.smAndDown"
        type="button"
        class="drawer-close"
        aria-label="Cerrar menú"
        @click="drawer = false"
      >
        <v-icon aria-hidden="true">close</v-icon>
      </button>
    </div>
    <vue-perfect-scrollbar
      class="drawer-menu--scroll"
      :settings="scrollSettings"
    >
      <nav
        v-if="sections.length"
        aria-label="Menú principal"
        class="drawer-navigation"
      >
        <v-list expand dense class="drawer-list">
          <section
            v-for="section in sections"
            :key="section.id"
            class="drawer-section"
            :aria-labelledby="section.title ? 'navigation-' + section.id : null"
          >
            <h2
              v-if="section.title"
              :id="'navigation-' + section.id"
              class="drawer-section-title"
            >
              {{ section.title }}
            </h2>
            <template v-for="(item, i) in section.items">
              <list-group
                v-if="item.group"
                :key="`${section.id}-group-${i}`"
                :item="item"
              />
              <list-item
                v-else
                :key="`${section.id}-item-${i}`"
                :icon="item.icon"
                :text="item.text"
                :to="item.to"
              />
            </template>
          </section>
        </v-list>
      </nav>
      <div v-else class="drawer-empty">
        <v-icon size="32" color="rgba(255,255,255,0.35)">menu_open</v-icon>
        <p>Sin menús asignados</p>
      </div>
    </vue-perfect-scrollbar>
  </v-navigation-drawer>
</template>
<script>
import { mapGetters } from 'vuex'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import ListGroup from '@/components/List/Group'
import ListItem from '@/components/List/Item'
import SessionHelper from '@/helpers/SessionHelper'
import { organizeNavigation } from '@/helpers/Navigation'

export default {
  name: 'AppDrawer',
  components: {
    VuePerfectScrollbar,
    ListGroup,
    ListItem,
  },
  props: {
    expanded: {
      type: Boolean,
      default: true,
    },
    drawWidth: {
      type: [Number, String],
      default: '260',
    },
    showDrawer: {
      immediate: true,
    },
  },
  data() {
    return {
      mini: false,
      drawer: true,
      scrollSettings: {
        maxScrollbarLength: 160,
      },
    }
  },

  computed: {
    ...mapGetters('session', ['userMenu', 'isAdmin']),
    items() {
      const tree = Array.isArray(this.userMenu) ? this.userMenu : []
      const normalized = tree
        .filter(node => node && (node.isActive == null || node.isActive))
        .map(node => this.normalizeMenuItem(node))
      if (SessionHelper.role === 'DIST') {
        const clientItems = normalized.filter(
          item =>
            !(
              (item.to || '').startsWith('/transit/') ||
              (item.children || []).some(
                child =>
                  (child.to || '').startsWith('/transit/') ||
                  (child.to || '') === '/contabilidad/cliente'
              )
            )
        )
        clientItems.push(
          {
            text: 'Gestión Aduanera',
            icon: 'assignment',
            group: 'client-transit',
            children: [
              {
                text: 'Trámites para Procesar',
                to: '/transit/ver-facturas?etapa=pending',
              },
              {
                text: 'Trámites en Proceso',
                to: '/transit/ver-facturas?etapa=process',
              },
              {
                text: 'Aprobaciones',
                to: '/transit/ver-facturas?etapa=approved',
              },
              { text: 'Rechazos', to: '/transit/ver-facturas?etapa=denied' },
              {
                text: 'Trámites para Reingreso',
                to: '/transit/ver-facturas?etapa=reentered',
              },
            ],
          },
          {
            text: 'Contabilidad',
            icon: 'account_balance',
            to: '/contabilidad/cliente',
          }
        )
        return this.filterAllowedMenu(clientItems)
      }
      const logisticsRoles = ['GEM', 'ADM', 'TRA', 'MENSAJERO', 'ENT']
      if (logisticsRoles.includes(SessionHelper.role)) {
        const other = normalized.filter(
          item =>
            !['Envíos', 'Envios', 'Logística'].includes(item.text) &&
            !(item.children || []).some(child =>
              /^\/(envios|logistica)/.test(child.to || '')
            )
        )
        other.push({
          text: 'Logística',
          icon: 'local_shipping',
          group: 'logistica',
          children: [
            { text: 'Mensajería', to: '/logistica/mensajeria' },
            { text: 'Transportista', to: '/logistica/transportista' },
          ],
        })
        return this.filterAllowedMenu(this.injectAdminExtras(other))
      }
      return this.injectAdminExtras(normalized)
    },
    computeLogo() {
      return '/static/logo-01-letra.png'
    },
    sections() {
      if (['DIST', 'LAB'].includes(SessionHelper.role)) {
        const items = [
          { text: 'Inicio', to: '/dashboard', icon: 'dashboard' },
          { text: 'Mis trámites', to: '/cliente/tramites', icon: 'assignment' },
          {
            text: 'Mis pendientes',
            to: '/cliente/pendientes',
            icon: 'assignment_late',
          },
          {
            text: 'Mis documentos',
            to: '/cliente/documentos',
            icon: 'folder_open',
          },
          {
            text: 'Mis entregas',
            to: '/cliente/entregas',
            icon: 'local_shipping',
          },
          {
            text: 'Mis facturas',
            to:
              SessionHelper.role === 'DIST'
                ? '/contabilidad/cliente'
                : '/contabilidad/estado-de-cuenta',
            icon: 'receipt',
          },
          {
            text: 'Notificaciones',
            to: '/cliente/notificaciones',
            icon: 'notifications_none',
          },
          { text: 'Mi empresa', to: '/cliente/empresa', icon: 'business' },
        ]
        const existing = organizeNavigation(this.items)
        const links = new Set(items.map(i => i.to))
        const extras = existing
          .reduce((all, section) => all.concat(section.items), [])
          .filter(item => !links.has(item.to))
        return [
          { id: 'cliente', title: '', items: this.filterAllowedMenu(items) },
          ...(extras.length
            ? [{ id: 'cliente-otros', title: 'Otros accesos', items: extras }]
            : []),
        ]
      }
      return organizeNavigation(this.items)
    },
  },
  watch: {
    drawer(visible) {
      this.$emit('visibility-change', visible)
    },
    showDrawer: {
      handler(val) {
        this.drawer = val
      },
      immediate: true,
    },
  },
  methods: {
    filterAllowedMenu(items) {
      return items.reduce((visible, item) => {
        if (Array.isArray(item.children)) {
          const children = this.filterAllowedMenu(item.children)
          if (children.length) visible.push({ ...item, children })
          return visible
        }
        if (!item.to) return visible
        const { route } = this.$router.resolve(item.to)
        if (
          !route.matched.length ||
          route.matched.some(record => record.path === '*')
        )
          return visible
        const restricted = route.matched
          .slice()
          .reverse()
          .find(record => Array.isArray(record.meta.roles))
        if (!restricted || restricted.meta.roles.includes(SessionHelper.role))
          visible.push(item)
        return visible
      }, [])
    },
    injectAdminExtras(items) {
      // El menú lateral es dinámico (viene del backend). Mientras estos ítems
      // no existan como registro en la BD, los inyectamos aquí para el rol GEM.
      // Si luego se crean en "Gestión de Menús", no se duplican (match por ruta).
      if (!this.isAdmin) return items

      const extras = [
        {
          text: 'Transportistas',
          icon: 'local_shipping',
          to: '/admin/transportistas',
        },
        {
          text: 'Motivos de pendiente',
          icon: 'flag',
          to: '/admin/motivos-pendientes',
        },
        {
          text: 'Plantillas de Bitácora',
          icon: 'fact_check',
          to: '/admin/bitacoras',
        },
      ]

      const isAdminGroup = group =>
        Array.isArray(group.children) &&
        (/administraci/i.test(group.text || '') ||
          group.children.some(c => (c.to || '').startsWith('/admin/')))

      const adminGroup = items.find(isAdminGroup)
      if (adminGroup) {
        extras.forEach(extra => {
          const exists = adminGroup.children.some(c => c.to === extra.to)
          if (!exists) adminGroup.children.push(extra)
        })
        return items
      }

      // Si no hay grupo de Administración en el menú dinámico, lo agregamos.
      return items.concat([
        {
          text: 'Administración',
          icon: 'settings',
          group: 'menu-admin-extra',
          children: extras,
        },
      ])
    },
    normalizeMenuItem(node) {
      const children = Array.isArray(node.children) ? node.children : []
      const visibleChildren = children.filter(
        c => c && (c.isActive == null || c.isActive)
      )
      const text = node.title || node.text || ''
      const icon =
        node.icon || (visibleChildren.length ? 'folder' : 'fiber_manual_record')

      if (visibleChildren.length) {
        return {
          text,
          icon,
          group: 'menu-' + (node.id != null ? node.id : text.toLowerCase()),
          children: visibleChildren.map(c => this.normalizeMenuItem(c)),
        }
      }
      return {
        text,
        icon,
        to: node.route || node.to || null,
      }
    },
    genChildTarget(item, subItem) {
      if (subItem.href) return
      if (subItem.component) {
        return {
          name: subItem.component,
        }
      }
      return { name: `${item.group}/${subItem.name}` }
    },
  },
}
</script>

<style lang="stylus" scoped>
$drawer-background = #0f172a
$drawer-text = #d5dfed
$drawer-muted = #a8bad1
$drawer-active = #254f87

.app--drawer
  background: $drawer-background !important
  overflow: hidden
  border-right: 1px solid #223047 !important

  /deep/ .v-navigation-drawer__content
    background: $drawer-background

  .drawer-brand
    height: 72px
    display: flex
    align-items: center
    justify-content: space-between
    padding: 0 20px
    border-bottom: 1px solid #263247

  .drawer-brand__logo
    object-fit: contain
    width: 180px
    max-width: 100%

  .drawer-close
    display: inline-flex
    align-items: center
    justify-content: center
    flex: 0 0 44px
    width: 44px
    height: 44px
    margin-right: -10px
    color: $drawer-text
    border-radius: 8px

  .drawer-menu--scroll
    height: calc(100% - 72px)
    overflow-y: auto
    overflow-x: hidden
    overscroll-behavior: contain

  .drawer-navigation
    padding: 12px 0 28px

  .drawer-section + .drawer-section
    margin-top: 20px

  .drawer-section-title
    color: $drawer-muted
    font-size: 12px
    font-weight: 600
    line-height: 20px
    margin: 0 20px 6px

  .drawer-empty
    padding: 48px 24px
    color: $drawer-muted
    text-align: center

  /deep/ .v-list
    padding: 0
    background: transparent !important

  /deep/ .v-list__tile
    margin: 2px 8px
    padding: 10px 12px
    min-height: 44px
    height: auto !important
    border-radius: 8px
    color: $drawer-text !important
    transition: background-color .16s ease, color .16s ease

  /deep/ .v-list__tile__action
    min-width: 32px

  /deep/ .v-list__tile__title
    height: auto
    line-height: 20px
    white-space: normal
    overflow-wrap: break-word

  /deep/ .v-list__tile__title span
    font-size: 14px
    line-height: 20px

  /deep/ .v-list__tile .v-icon
    color: $drawer-muted !important
    font-size: 21px

  /deep/ .v-list__tile:hover,
  /deep/ .drawer-group-trigger:hover,
  .drawer-close:hover
    background: #1c2b43
    color: #ffffff !important

  /deep/ .drawer-link--current
    background: $drawer-active !important
    color: #ffffff !important

  /deep/ .drawer-link--current .v-icon
    color: #ffffff !important

  /deep/ .drawer-link--current .v-list__tile__title span
    font-weight: 600

  /deep/ .v-list__group__header
    display: block
    padding: 0
    min-height: 0

  /deep/ .v-list__group__header:after,
  /deep/ .v-list__group__header:before,
  /deep/ .v-list__group:after,
  /deep/ .v-list__group:before
    display: none

  /deep/ .drawer-group-trigger
    display: flex
    align-items: center
    gap: 10px
    width: calc(100% - 16px)
    min-height: 44px
    margin: 2px 8px
    padding: 10px 12px
    color: $drawer-text
    border-radius: 8px
    font: inherit
    font-size: 14px
    line-height: 20px
    text-align: left
    cursor: pointer
    transition: background-color .16s ease

  /deep/ .drawer-group-trigger > span
    flex: 1
    min-width: 0

  /deep/ .drawer-group-trigger .v-icon
    color: $drawer-muted
    font-size: 21px
    flex: 0 0 22px

  /deep/ .drawer-group--current > .v-list__group__header .drawer-group-trigger
    color: #ffffff
    background: #1b2d49

  /deep/ .drawer-group--current > .v-list__group__header .drawer-group-trigger > span
    font-weight: 600

  /deep/ .drawer-group-trigger .drawer-group-chevron
    font-size: 18px
    transition: transform .16s ease

  /deep/ .drawer-group-trigger[aria-expanded='true'] .drawer-group-chevron
    transform: rotate(180deg)

  /deep/ .v-list__group__items
    padding: 2px 0 6px
    margin: 0
    background: transparent

  /deep/ .v-list__group__items .v-list__tile
    padding-left: 44px
    color: #c5d2e4 !important

  /deep/ .v-list__group__items .v-list__tile .v-list__tile__action
    display: none

  /deep/ .v-list__group__items .drawer-link--current
    color: #ffffff !important

  /deep/ .drawer-group--nested .drawer-group-trigger
    padding-left: 28px

  /deep/ a:focus-visible,
  /deep/ button:focus-visible,
  .drawer-close:focus-visible
    outline: 2px solid #e5bc59
    outline-offset: -2px

@media (prefers-reduced-motion: reduce)
  .app--drawer
    /deep/ .v-list__tile,
    /deep/ .drawer-group-trigger,
    /deep/ .drawer-group-chevron
      transition: none
</style>
